import React, { useEffect, useState } from "react";
import { Provider } from "mobx-react";
// Sentry disabled (subscription cancelled). Uncomment together with the init block below to re-enable.
// import * as Sentry from "@sentry/gatsby";
import loadable from "@loadable/component";
import stores from "../stores";
import Layout from "./layout/Layout";

// eslint-disable-next-line import/order

// import GoogleConsentModal from "@components/page/GoogleConsentModal"; // Need to be below the "stores" and "AuthUserService" imports
import { getCookie } from "../utils/functions";
import "react-calendar/dist/Calendar.css";

const getRequestParamsObj = () => {
  if (typeof window !== "undefined") {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");

    const requestObj = {
      utm_source: utmSource || "",
      utm_medium: utmMedium || "",
      utm_campaign: utmCampaign || ""
    };
    return requestObj;
  }
  return {};
};

const setCookie = (name, value, days, configName) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  if (configName === "boleron") {
    document.cookie = `${name}=${value || ""}${expires};domain=.boleron.bg;path=/`;
  } else if (configName === "vivacom") {
    document.cookie = `${name}=${value || ""}${expires};domain=.vivacom.bg;path=/`;
  }
};

const FireBaseApp = ({ element, showElementDirectly, enableAuth }) => {
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    if (!enableAuth) {
      setLoggedIn(true);
      return;
    }

    let isMounted = true;

    const initAuth = async () => {
      const { default: AuthUserService } = await import("../services/AuthUserService");
      AuthUserService.subscribeAuthChange(async user => {
        if (!isMounted) return;
        if (user) {
          // ако вече е логнат добавяме юзъра в state
          stores.auth.setUser(user);
          if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "login",
              userId: user.uid
            });
          }
        } else {
          // ако не е логнат създаваме анонимен потребител
          try {
            await AuthUserService.loginAnonymous();
          } catch (err) {
            console.log("err", err);
          }
        }

        setLoggedIn(true);
      });
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  return <Provider {...stores}>{loggedIn || showElementDirectly ? element : <Layout title=" " navbarWorkflow loading={true} />}</Provider>;
};

const Wrapper = ({ element }) => {
  let showElementDirectly = true;
  let isShowConsent = false;

  const GoogleConsentModal = loadable(() => import("@components/page/GoogleConsentModal"));

  let enableAuth = true;

  useEffect(() => {
    const GATSBY_CONFIGURATION = process.env.GATSBY_CONFIGURATION || "boleron";

    const requestParams = getRequestParamsObj();

    if (
      (GATSBY_CONFIGURATION === "vivacom" && requestParams?.utm_source) ||
      requestParams.utm_source === "android" ||
      requestParams.utm_source === "ios" ||
      requestParams.utm_source === "biala_karta" ||
      requestParams.utm_source === "easy_credit" ||
      requestParams.utm_source === "viva_credit" ||
      requestParams.utm_source === "bulsatcom" ||
      requestParams.utm_source === "ultrafan" ||
      requestParams.utm_source === "icard"
    ) {
      const encoded = btoa(JSON.stringify(requestParams));
      const days = GATSBY_CONFIGURATION === "boleron" ? 90 : 7;
      setCookie("boleron_affiliate", encoded, days, GATSBY_CONFIGURATION);
    }
  }, []);

  if (typeof window !== "undefined") {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const pathName = window?.location?.pathname;
    const skipAuthPaths = [
      "/grazhdanska-otgovornost-online",
      "/kasko-kalkulator",
      "/kasko-plus-grazhdanska",
      "/zastrahovka-patuvane-v-chuzhbina",
      "/zastrahovka-imushtestvo",
      "/planinska-zastrahovka",
      "/zastrahovka-ekstremni-sportove",
      "/zastrahovki-zhivot",
      "/zdravni-zastrahovki",
      "/firmeni-zastrahovki",
      "/dopulnitelno-zdravno-bulstrad",
      "/zdravna-zastrahovka-health-club",
      "/zdravna-zastrahovka-for-foreigners",
      "/zastrahovka-bolnichen-prestoi",
      "/zastrahovka-jivot",
      "/zastrahovka-zhivot-investitsionen-fond",
      "/proverka-elektronna-vinetka",
      "/proverka-globi-kat-grazhdanska",
      "/proverka-grazhdanska",
      "/proverka-tehnicheski-pregled"
    ];
    enableAuth = !skipAuthPaths.includes(pathName);
    const patterns = ["policy-payment", "workflow"];
    showElementDirectly = !patterns.some(pattern => pathName.includes(pattern));
    const utmMedium = urlParams.get("utm_medium");
    const utmSource = urlParams.get("utm_source");

    if (utmMedium === "email") {
      window.sessionStorage.setItem("utm_medium", "email");
    }
    const hasUserAcceptedConsent = getCookie("boleron-google-consent");
    const isReactNativeWebView = window ? window.sessionStorage.getItem("hideHeader") === "true" : false;
    const isEmailSet = window.sessionStorage.getItem("utm_medium") === "email";
    const isMobileApp = utmSource === "ios" || utmSource === "android";
    const gatsbyConfiguration = process.env.GATSBY_CONFIGURATION || "boleron";
    isShowConsent = gatsbyConfiguration === "boleron" && !hasUserAcceptedConsent && !isReactNativeWebView && !isEmailSet && !isMobileApp;

    if (hasUserAcceptedConsent) {
      try {
        if (typeof window !== "undefined") {
          if (window.dataLayer && !window.gtag) {
            window.gtag = function () {
              dataLayer.push(arguments);
            };
          }
          if (typeof window.gtag === "function") {
            window.gtag("consent", "update", {
              ad_storage: "granted",
              ad_user_data: "granted",
              ad_personalization: "granted",
              analytics_storage: "granted",
              wait_for_update: 500
            });
          }
        }
      } catch (err) {
        console.log(err);
      }

      // Sentry disabled (subscription cancelled).
      // if (process.env.GATSBY_ENV === "PRODUCTION") {
      //   const initSentry = () => {
      //     Sentry.init({
      //       dsn: "https://552b1013a1fc2a3e92ec889d2d2be781@o4508720138747904.ingest.de.sentry.io/4508720143925328",
      //
      //       environment: "production",
      //       integrations: [Sentry.browserTracingIntegration()],
      //       // Tracing
      //       tracesSampleRate: 1.0, //  Capture 100% of the transactions
      //       // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      //       tracePropagationTargets: ["*"],
      //       // tracePropagationTargets:  ["localhost", /^https:\/\/api\.boleron\.bg/],
      //       // Session Replay
      //       replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      //       replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      //     });
      //   };
      //
      //   if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      //     window.requestIdleCallback(initSentry, { timeout: 2000 });
      //   } else {
      //     setTimeout(initSentry, 0);
      //   }
      // }
    }
  }

  return (
    <>
      <GoogleConsentModal isShowConsent={isShowConsent} />

      <FireBaseApp element={element} showElementDirectly={showElementDirectly} enableAuth={enableAuth} />
    </>
  );
};

export default Wrapper;
